import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { GiOakLeaf } from 'react-icons/gi';

const NavBar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    // Handle scroll effect for glassmorphism
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => setIsOpen(!isOpen);

    const navLinks = [
        { name: 'O nas', path: '/' },
        { name: 'Usługi', path: '/#services', isHash: true },
    ];

    return (
        <nav
            className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' : 'bg-white/90 backdrop-blur-sm shadow-sm py-4'}`}
            role="navigation"
            aria-label="Główna nawigacja"
        >
            {/* Decorative top border */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-niwy-secondary via-niwy-accent to-niwy-primary opacity-80" aria-hidden="true"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo with leaf decoration */}
                    <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
                        <GiOakLeaf className={`text-niwy-secondary transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110 ${scrolled ? 'w-6 h-6' : 'w-8 h-8'}`} aria-hidden="true" />
                        <div className="flex flex-col">
                            <span className={`font-serif font-bold tracking-wide text-niwy-primary transition-all duration-300 ${scrolled ? 'text-xl' : 'text-2xl'}`}>
                                Niwy Natury
                            </span>
                            <span className={`text-niwy-secondary text-xs font-light italic tracking-wider transition-all duration-300 ${scrolled ? 'opacity-0 h-0' : 'opacity-100'}`}>
                                Pracownia Zdrowia
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex space-x-8 items-center">
                        {navLinks.map((link) => {
                            const isHome = location.pathname === '/';

                            if (link.isHash && isHome) {
                                return (
                                    <a
                                        key={link.name}
                                        href={link.path.replace('/', '')}
                                        className="relative text-base font-medium transition-all duration-300 hover:text-niwy-secondary text-niwy-primary group py-2"
                                    >
                                        {link.name}
                                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-niwy-accent transition-all duration-300 group-hover:w-full" aria-hidden="true"></span>
                                    </a>
                                );
                            }

                            return (
                                <HashLink
                                    key={link.name}
                                    smooth
                                    to={link.path}
                                    className="relative text-base font-medium transition-all duration-300 hover:text-niwy-secondary text-niwy-primary group py-2"
                                >
                                    {link.name}
                                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-niwy-accent transition-all duration-300 group-hover:w-full" aria-hidden="true"></span>
                                </HashLink>
                            );
                        })}
                        <Link
                            to="/kontakt"
                            className="px-6 py-2.5 rounded-full text-white font-medium transition-all transform hover:scale-105 bg-niwy-primary hover:bg-niwy-primary/90"
                        >
                            <span className="flex items-center gap-2">
                                <GiOakLeaf className="w-4 h-4 transition-transform group-hover:rotate-45" aria-hidden="true" />
                                Umów wizytę
                            </span>
                        </Link>
                    </div>

                    {/* Mobile Menu Button - Enhanced for accessibility */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={handleClick}
                            aria-label={isOpen ? "Zamknij menu nawigacji" : "Otwórz menu nawigacji"}
                            aria-expanded={isOpen}
                            aria-controls="mobile-menu"
                            className="p-2 rounded-lg text-niwy-primary hover:bg-niwy-bg-secondary transition-colors duration-200 focus:outline-2 focus:outline-offset-2 focus:outline-niwy-accent"
                        >
                            <div className="w-6 h-6 flex flex-col justify-center items-center" aria-hidden="true">
                                <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-1' : '-translate-y-1'}`}></span>
                                <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                                <span className={`block w-5 h-0.5 bg-current transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1'}`}></span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown - Enhanced for accessibility */}
            <div
                id="mobile-menu"
                className={`md:hidden absolute top-full left-0 w-full bg-white shadow-xl transition-all duration-400 ease-in-out transform origin-top ${isOpen ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0 pointer-events-none'}`}
                aria-hidden={!isOpen}
            >
                {/* Decorative element */}
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-niwy-secondary via-niwy-accent to-niwy-primary" aria-hidden="true"></div>

                <div className="px-6 pt-6 pb-8 space-y-1 flex flex-col items-center">

                    {navLinks.map((link, index) => {
                        const isHome = location.pathname === '/';

                        if (link.isHash && isHome) {
                            return (
                                <React.Fragment key={link.name}>
                                    <a
                                        href={link.path.replace('/', '')}
                                        onClick={() => setIsOpen(false)}
                                        className="block px-6 py-3 text-lg font-medium text-niwy-text hover:text-niwy-primary rounded-lg w-full text-center transition-all duration-200"
                                    >
                                        {link.name}
                                    </a>
                                    <div className="w-32 h-px bg-niwy-accent/40 my-1" aria-hidden="true"></div>
                                </React.Fragment>
                            );
                        }

                        return (
                            <React.Fragment key={link.name}>
                                {link.isHash ? (
                                    <HashLink
                                        smooth
                                        to={link.path}
                                        onClick={() => setIsOpen(false)}
                                        className="block px-6 py-3 text-lg font-medium text-niwy-text hover:text-niwy-primary rounded-lg w-full text-center transition-all duration-200"
                                    >
                                        {link.name}
                                    </HashLink>
                                ) : (
                                    <Link
                                        to={link.path}
                                        onClick={() => setIsOpen(false)}
                                        className="block px-6 py-3 text-lg font-medium text-niwy-text hover:text-niwy-primary rounded-lg w-full text-center transition-all duration-200"
                                    >
                                        {link.name}
                                    </Link>
                                )}
                                <div className="w-32 h-px bg-niwy-accent/40 my-1" aria-hidden="true"></div>
                            </React.Fragment>
                        );
                    })}

                    <Link
                        to="/kontakt"
                        onClick={() => setIsOpen(false)}
                        className="px-6 py-3 text-lg font-medium text-white bg-niwy-primary hover:bg-niwy-primary/90 rounded-full w-full text-center mt-4 transition-all duration-200"
                    >
                        Umów wizytę
                    </Link>
                </div>
            </div>
        </nav>
    );
};

export default NavBar;
